import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StreamingText } from "./StreamingText";
import { AgentToolBlock } from "./AgentToolBlock";
import { UserInterviewBlock } from "./UserInterviewBlock";
import { SlideOperationsApprovalBlock } from "./SlideOperationsApprovalBlock";
/**
 * Renders a single message component.
 * This is memoized so React only re-renders when the component actually changes!
 * This is the MVVM-style approach - each component renders independently.
 */
export const MessageComponentRenderer = memo(({ component, messageId, isStreaming }) => {
    const hasMarkdown = (text) => {
        const markdownPatterns = [
            /^#{1,6}\s+/m, // Headers
            /\*\*.*?\*\*/, // Bold
            /\*.*?\*/, // Italic
            /`.*?`/, // Inline code
            /```[\s\S]*?```/, // Code blocks
            /^\s*[-*+]\s+/m, // Unordered lists
            /^\s*\d+\.\s+/m, // Ordered lists
            /\[.*?\]\(.*?\)/, // Links
            /^>\s+/m, // Blockquotes
        ];
        return markdownPatterns.some(pattern => pattern.test(text));
    };
    // For text components, check if markdown is needed
    if (component.type === 'text') {
        const content = component.content;
        // Render with markdown if needed
        if (hasMarkdown(content)) {
            return (_jsx("div", { className: "markdown-content leading-relaxed", children: _jsx(StreamingText, { text: content, isStreaming: isStreaming, messageId: `${messageId}-${component.msg_component_id}`, children: (displayedText) => (_jsx(_Fragment, { children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: {
                                h1: ({ children }) => _jsx("h1", { className: "text-base font-bold mt-4 mb-2 text-gray-900", children: children }),
                                h2: ({ children }) => _jsx("h2", { className: "text-base font-bold mt-3 mb-2 text-gray-900", children: children }),
                                h3: ({ children }) => _jsx("h3", { className: "text-base font-bold mt-3 mb-2 text-gray-900", children: children }),
                                p: ({ children }) => _jsx("p", { className: "mb-3 leading-relaxed text-base", children: children }),
                                ul: ({ children }) => _jsx("ul", { className: "list-disc list-outside mb-3 space-y-1 pl-6", children: children }),
                                ol: ({ children }) => _jsx("ol", { className: "list-decimal list-outside mb-3 space-y-1 pl-6", children: children }),
                                li: ({ children }) => _jsx("li", { className: "leading-relaxed [&>p]:inline [&>p]:m-0", children: children }),
                                strong: ({ children }) => _jsx("strong", { className: "font-bold text-gray-900", children: children }),
                                em: ({ children }) => _jsx("em", { className: "italic", children: children }),
                                code: ({ children, className }) => {
                                    const isInline = !className;
                                    return isInline ? (_jsx("code", { className: "bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800", children: children })) : (_jsx("code", { className: className, children: children }));
                                },
                                pre: ({ children }) => (_jsx("pre", { className: "bg-gray-100 p-3 rounded-lg overflow-x-auto mb-3 text-sm font-mono", children: children })),
                                blockquote: ({ children }) => (_jsx("blockquote", { className: "border-l-4 border-gray-300 pl-4 italic my-3 text-gray-700", children: children })),
                                table: ({ children }) => (_jsx("div", { className: "my-3 overflow-hidden rounded-lg border border-gray-300", children: _jsx("table", { className: "w-full border-collapse text-sm", children: children }) })),
                                thead: ({ children }) => _jsx("thead", { className: "bg-gray-100", children: children }),
                                th: ({ children }) => (_jsx("th", { className: "border-b border-gray-300 px-3 py-2 text-left font-semibold text-gray-800", children: children })),
                                td: ({ children }) => (_jsx("td", { className: "border-t border-gray-200 px-3 py-2 align-top text-gray-800", children: children })),
                                hr: () => _jsx("hr", { className: "my-4 border-gray-300" }),
                                a: ({ href, children }) => (_jsx("a", { href: href, target: "_blank", rel: "noopener noreferrer", className: "text-blue-600 hover:underline", children: children })),
                            }, children: displayedText }) })) }) }));
        }
        // Plain text without markdown - still use StreamingText for animation!
        return (_jsx("div", { className: "leading-relaxed text-base", children: _jsx(StreamingText, { text: content, isStreaming: isStreaming, messageId: `${messageId}-${component.msg_component_id}`, children: (displayedText) => (_jsx(_Fragment, { children: displayedText })) }) }));
    }
    // For tool_start and tool_complete components, try to render AgentToolBlock
    if (component.type === 'tool_start' || component.type === 'tool_complete') {
        try {
            // Content should be JSON-stringified AgentToolData
            const toolData = JSON.parse(component.content);
            return _jsx(AgentToolBlock, { toolData: toolData });
        }
        catch (e) {
            // Fallback to simple display if not valid JSON
            return (_jsxs("div", { className: "leading-relaxed text-base text-gray-600", children: [component.displayVerb || 'Tool', ": ", component.content] }));
        }
    }
    // For user_interview components, render UserInterviewBlock
    // Wrapped with my-2 for consistent spacing from adjacent chat content
    if (component.type === 'user_interview') {
        try {
            const interviewData = JSON.parse(component.content);
            return (_jsx("div", { className: "my-2", children: _jsx(UserInterviewBlock, { data: interviewData, onSubmit: (answers) => {
                        // Emit custom event that ppt-addin listens for
                        const event = new CustomEvent('carousel:interview-submit', {
                            detail: { interviewId: interviewData.id, answers }
                        });
                        window.dispatchEvent(event);
                    } }) }));
        }
        catch (e) {
            return (_jsxs("div", { className: "leading-relaxed text-base text-gray-600", children: ["Interview: ", component.content] }));
        }
    }
    // For slide_operations_approval components, render SlideOperationsApprovalBlock
    // Wrapped with my-2 for consistent spacing from adjacent chat content
    if (component.type === 'slide_operations_approval') {
        try {
            const approvalData = JSON.parse(component.content);
            return (_jsx("div", { className: "my-2", children: _jsx(SlideOperationsApprovalBlock, { data: approvalData, onSubmit: (approvalDecisions) => {
                        // Emit custom event that ppt-addin listens for
                        // Convert Map to array of [index, approved] for serialization
                        const decisionsArray = Array.from(approvalDecisions.entries());
                        const event = new CustomEvent('carousel:slide-ops-submit', {
                            detail: { approvalId: approvalData.id, decisions: decisionsArray }
                        });
                        window.dispatchEvent(event);
                    }, onCancel: () => {
                        // Emit cancel event
                        const event = new CustomEvent('carousel:slide-ops-cancel', {
                            detail: { approvalId: approvalData.id }
                        });
                        window.dispatchEvent(event);
                    } }) }));
        }
        catch (e) {
            return (_jsxs("div", { className: "leading-relaxed text-base text-gray-600", children: ["Slide Operations: ", component.content] }));
        }
    }
    // For error components, render with error styling
    if (component.type === 'error') {
        return (_jsx("div", { className: "leading-relaxed text-base text-red-600 bg-red-50 p-3 rounded-lg border border-red-200", children: component.content }));
    }
    // Fallback for any other component types
    return (_jsx("div", { className: "leading-relaxed text-base", children: component.content }));
}, (prevProps, nextProps) => {
    // Custom comparison function for memo
    // Return TRUE to skip re-render (props are equal)
    // Return FALSE to allow re-render (props changed)
    // For streaming text, ALWAYS re-render when content changes (even slightly)
    // This ensures we see each chunk as it arrives from Claude
    const contentChanged = prevProps.component.content !== nextProps.component.content;
    const typeChanged = prevProps.component.type !== nextProps.component.type;
    const streamingChanged = prevProps.isStreaming !== nextProps.isStreaming;
    // If anything changed, re-render (return false)
    if (contentChanged || typeChanged || streamingChanged) {
        return false; // Allow re-render
    }
    // Nothing changed, skip re-render (return true)
    return true;
});
MessageComponentRenderer.displayName = 'MessageComponentRenderer';
